// components/models/KlingControlPanel.tsx
import React, { useCallback } from 'react';
import RangeControl from '../ui/RangeControl';
import AutoGrowingTextarea from '../AutoGrowingTextarea';
import Select from '../ui/Select';
import Switch from '../ui/Switch';
import VideoInputRangeControls from './VideoInputRangeControls';
import { Kling3Params, Kling3OmniParams, VideoReferenceInputMode } from '#types/types';
import { useKernel } from '@/kernel/Kernel';
import { AppTooltip } from '../ui/tooltip';

interface Kling3ControlPanelProps {
  params: Kling3Params;
  setModelParams: (params: (prevParams: { [model: string]: Kling3Params }) => { [model: string]: Kling3Params }) => void;
  diffusionModel: string;
}

interface Kling3OmniControlPanelProps {
  params: Kling3OmniParams;
  setModelParams: (params: (prevParams: { [model: string]: Kling3OmniParams }) => { [model: string]: Kling3OmniParams }) => void;
  diffusionModel: string;
}

interface KlingControlPanelProps {
  params: Kling3Params | Kling3OmniParams;
  setModelParams: (params: (prevParams: { [model: string]: Kling3Params | Kling3OmniParams }) => { [model: string]: Kling3Params | Kling3OmniParams }) => void;
  diffusionModel: string;
}

const defaultKling3Params: Kling3Params = {
  prompt: '',
  negative_prompt: '',
  duration: 5,
  resolution: '1080p',
  generate_audio: false,
  seed: -1,
};

const defaultKling3OmniParams: Kling3OmniParams = {
  prompt: '',
  negative_prompt: '',
  duration: 5,
  resolution: '1080p',
  generate_audio: false,
  keep_original_sound: true,
  video_reference_type: 'base',
  reference_input_mode: 'frame',
  video_input_start_ms: null,
  video_input_end_ms: null,
  seed: -1,
};

export const getDefaultKling3Params = (): Kling3Params => {
  return { ...defaultKling3Params };
};

export const getDefaultKling3OmniParams = (): Kling3OmniParams => {
  return { ...defaultKling3OmniParams };
};

const REFERENCE_INPUT_MODES: VideoReferenceInputMode[] = ['frame', 'video'];
const RESOLUTION_OPTIONS = ['720p', '1080p'];
const VIDEO_REFERENCE_TYPE_OPTIONS = ['feature', 'base'];
const VIDEO_REFERENCE_TYPE_HINT = "How to use reference video: 'feature' for style/camera reference, 'base' for video editing. Fater defaults to 'base'.";
const KLING_TOOLTIP_CLASS = 'max-w-[270px] border border-white/[0.06] bg-[#353535] px-3 py-2 text-xs leading-relaxed text-white/75 shadow-2xl';

const Kling3ControlPanel: React.FC<Kling3ControlPanelProps> = React.memo(({
  params, setModelParams, diffusionModel,
}) => {
  const { kernel } = useKernel();

  const handleParamChange = useCallback((
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value, type } = e.target;
    const newValue = type === 'number' || type === 'range' ? parseFloat(value) : value;

    setModelParams(prevModelParams => {
      const currentParams = prevModelParams[diffusionModel];
      if (!currentParams) {
        console.error(`[Kling3ControlPanel] handleParamChange: Could not find params for model "${diffusionModel}". Aborting state update.`);
        return prevModelParams;
      }
      return {
        ...prevModelParams,
        [diffusionModel]: {
          ...currentParams,
          [name]: newValue,
        },
      };
    });

    if (name === 'prompt' || name === 'negative_prompt') {
      kernel.current?.logUserAction('Setting Changed', {
        setting_name: name,
        value_length: (newValue as string).length,
        model_name: diffusionModel,
        source_component: 'Kling3ControlPanel',
      });
    } else {
      kernel.current?.logUserAction('Setting Changed', {
        setting_name: name,
        value: newValue,
        model_name: diffusionModel,
        source_component: 'Kling3ControlPanel',
      });
    }
  }, [diffusionModel, kernel, setModelParams]);

  const handleSelectChange = useCallback((name: string, value: string) => {
    setModelParams(prevModelParams => {
      const currentParams = prevModelParams[diffusionModel];
      if (!currentParams) {
        console.error(`[Kling3ControlPanel] handleSelectChange: Could not find params for model "${diffusionModel}". Aborting state update.`);
        return prevModelParams;
      }
      return {
        ...prevModelParams,
        [diffusionModel]: {
          ...currentParams,
          [name]: value,
        },
      };
    });
    kernel.current?.logUserAction('Setting Changed', {
      setting_name: name,
      value,
      model_name: diffusionModel,
      source_component: 'Kling3ControlPanel',
    });
  }, [diffusionModel, kernel, setModelParams]);

  const handleResolutionChange = useCallback((value: string) => {
    handleSelectChange('resolution', value);
  }, [handleSelectChange]);

  const onSwitchGenerateAudio = useCallback(() => {
    const live = kernel.current?.getModelParams()[diffusionModel] as Kling3Params | undefined;
    const newValue = !live?.generate_audio;
    setModelParams(prevModelParams => {
      const currentParams = prevModelParams[diffusionModel];
      if (!currentParams) {
        console.error(`[Kling3ControlPanel] onSwitchGenerateAudio: Could not find params for model "${diffusionModel}". Aborting state update.`);
        return prevModelParams;
      }
      return {
        ...prevModelParams,
        [diffusionModel]: { ...currentParams, generate_audio: newValue },
      };
    });
    kernel.current?.logUserAction('Setting Changed', {
      setting_name: 'generate_audio',
      value: newValue,
      model_name: diffusionModel,
      source_component: 'Kling3ControlPanel',
    });
  }, [diffusionModel, kernel, setModelParams]);

  return (
    <>
      <div className="mb-4">
        <AutoGrowingTextarea
          label="Prompt"
          placeholder="Describe what you want to generate..."
          name="prompt"
          value={params.prompt}
          onChange={handleParamChange}
          onEnterPress={kernel.current?.triggerGenerate}
          maxHeight={600}
          labelClassName="block text-lg font-semibold text-white mb-2"
        />
      </div>

      <div className="mb-4">
        <AutoGrowingTextarea
          label="Negative Prompt"
          placeholder="Properties to avoid."
          name="negative_prompt"
          value={params.negative_prompt}
          onChange={handleParamChange}
          maxHeight={600}
          labelClassName="block text-lg font-semibold text-white mb-2"
        />
      </div>

      <div className="mb-4">
        <RangeControl
          label="Duration"
          name="duration"
          min={3}
          max={15}
          step={1}
          value={params.duration}
          onChange={handleParamChange}
          description={'In seconds.'}
        />
      </div>

      <div className="mb-4">
        <Select
          id="kling3-resolution"
          label="Resolution"
          value={params.resolution}
          options={RESOLUTION_OPTIONS}
          onChange={handleResolutionChange}
        />
      </div>

      <div className="flex items-center space-x-2 mb-4">
        <Switch
          checked={params.generate_audio}
          onChange={onSwitchGenerateAudio}
          labelRight="Generate Audio"
        />
      </div>
    </>
  );
});
Kling3ControlPanel.displayName = 'Kling3ControlPanel';

const Kling3OmniControlPanel: React.FC<Kling3OmniControlPanelProps> = React.memo(({
  params, setModelParams, diffusionModel,
}) => {
  const { kernel } = useKernel();
  const referenceInputMode: VideoReferenceInputMode =
    params.reference_input_mode === 'video' ? 'video' : 'frame';

  const handleParamChange = useCallback((
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value, type } = e.target;
    const newValue = type === 'number' || type === 'range' ? parseFloat(value) : value;

    setModelParams(prevModelParams => {
      const currentParams = prevModelParams[diffusionModel];
      if (!currentParams) {
        console.error(`[Kling3OmniControlPanel] handleParamChange: Could not find params for model "${diffusionModel}". Aborting state update.`);
        return prevModelParams;
      }
      return {
        ...prevModelParams,
        [diffusionModel]: {
          ...currentParams,
          [name]: newValue,
        },
      };
    });

    if (name === 'prompt' || name === 'negative_prompt') {
      kernel.current?.logUserAction('Setting Changed', {
        setting_name: name,
        value_length: (newValue as string).length,
        model_name: diffusionModel,
        source_component: 'Kling3OmniControlPanel',
      });
    } else {
      kernel.current?.logUserAction('Setting Changed', {
        setting_name: name,
        value: newValue,
        model_name: diffusionModel,
        source_component: 'Kling3OmniControlPanel',
      });
    }
  }, [diffusionModel, kernel, setModelParams]);

  const handleSelectChange = useCallback((name: string, value: string) => {
    setModelParams(prevModelParams => {
      const currentParams = prevModelParams[diffusionModel];
      if (!currentParams) {
        console.error(`[Kling3OmniControlPanel] handleSelectChange: Could not find params for model "${diffusionModel}". Aborting state update.`);
        return prevModelParams;
      }
      return {
        ...prevModelParams,
        [diffusionModel]: {
          ...currentParams,
          [name]: value,
        },
      };
    });
    kernel.current?.logUserAction('Setting Changed', {
      setting_name: name,
      value,
      model_name: diffusionModel,
      source_component: 'Kling3OmniControlPanel',
    });
  }, [diffusionModel, kernel, setModelParams]);

  const handleResolutionChange = useCallback((value: string) => {
    handleSelectChange('resolution', value);
  }, [handleSelectChange]);

  const handleVideoReferenceTypeChange = useCallback((value: string) => {
    handleSelectChange('video_reference_type', value);
  }, [handleSelectChange]);

  const onSwitchGenerateAudio = useCallback(() => {
    const live = kernel.current?.getModelParams()[diffusionModel] as Kling3OmniParams | undefined;
    const newValue = !live?.generate_audio;
    setModelParams(prevModelParams => {
      const currentParams = prevModelParams[diffusionModel];
      if (!currentParams) {
        console.error(`[Kling3OmniControlPanel] onSwitchGenerateAudio: Could not find params for model "${diffusionModel}". Aborting state update.`);
        return prevModelParams;
      }
      return {
        ...prevModelParams,
        [diffusionModel]: {
          ...currentParams,
          generate_audio: newValue,
          keep_original_sound: newValue ? false : currentParams.keep_original_sound,
        },
      };
    });
    kernel.current?.logUserAction('Setting Changed', {
      setting_name: 'generate_audio',
      value: newValue,
      model_name: diffusionModel,
      source_component: 'Kling3OmniControlPanel',
    });
    if (newValue) {
      kernel.current?.logUserAction('Setting Changed', {
        setting_name: 'keep_original_sound',
        value: false,
        model_name: diffusionModel,
        source_component: 'Kling3OmniControlPanel',
      });
    }
  }, [diffusionModel, kernel, setModelParams]);

  const onSwitchKeepOriginalSound = useCallback(() => {
    const live = kernel.current?.getModelParams()[diffusionModel] as Kling3OmniParams | undefined;
    const newValue = !live?.keep_original_sound;
    setModelParams(prevModelParams => {
      const currentParams = prevModelParams[diffusionModel];
      if (!currentParams) {
        console.error(`[Kling3OmniControlPanel] onSwitchKeepOriginalSound: Could not find params for model "${diffusionModel}". Aborting state update.`);
        return prevModelParams;
      }
      return {
        ...prevModelParams,
        [diffusionModel]: {
          ...currentParams,
          keep_original_sound: newValue,
          generate_audio: newValue ? false : currentParams.generate_audio,
        },
      };
    });
    kernel.current?.logUserAction('Setting Changed', {
      setting_name: 'keep_original_sound',
      value: newValue,
      model_name: diffusionModel,
      source_component: 'Kling3OmniControlPanel',
    });
    if (newValue) {
      kernel.current?.logUserAction('Setting Changed', {
        setting_name: 'generate_audio',
        value: false,
        model_name: diffusionModel,
        source_component: 'Kling3OmniControlPanel',
      });
    }
  }, [diffusionModel, kernel, setModelParams]);

  const handleReferenceInputModeChange = useCallback((value: string) => {
    const nextMode: VideoReferenceInputMode = value === 'video' ? 'video' : 'frame';
    const live = kernel.current?.getModelParams()[diffusionModel] as Kling3OmniParams | undefined;
    setModelParams(prevModelParams => {
      const currentParams = prevModelParams[diffusionModel];
      if (!currentParams) {
        console.error(`[Kling3OmniControlPanel] handleReferenceInputModeChange: Could not find params for model "${diffusionModel}". Aborting state update.`);
        return prevModelParams;
      }
      return {
        ...prevModelParams,
        [diffusionModel]: {
          ...currentParams,
          reference_input_mode: nextMode,
          generate_audio: nextMode === 'video' ? false : currentParams.generate_audio,
        },
      };
    });
    kernel.current?.logUserAction('Setting Changed', {
      setting_name: 'reference_input_mode',
      value: nextMode,
      model_name: diffusionModel,
      source_component: 'Kling3OmniControlPanel',
    });
    if (nextMode === 'video' && live?.generate_audio) {
      kernel.current?.logUserAction('Setting Changed', {
        setting_name: 'generate_audio',
        value: false,
        model_name: diffusionModel,
        source_component: 'Kling3OmniControlPanel',
      });
    }
  }, [diffusionModel, kernel, setModelParams]);

  const handleVideoInputRangeChange = useCallback((
    name: 'video_input_start_ms' | 'video_input_end_ms',
    value: number | null,
  ) => {
    setModelParams(prevModelParams => {
      const currentParams = prevModelParams[diffusionModel];
      if (!currentParams) {
        console.error(`[Kling3OmniControlPanel] handleVideoInputRangeChange: Could not find params for model "${diffusionModel}". Aborting state update.`);
        return prevModelParams;
      }
      return {
        ...prevModelParams,
        [diffusionModel]: {
          ...currentParams,
          [name]: value,
        },
      };
    });
    kernel.current?.logUserAction('Setting Changed', {
      setting_name: name,
      value,
      model_name: diffusionModel,
      source_component: 'Kling3OmniControlPanel',
    });
  }, [diffusionModel, kernel, setModelParams]);

  return (
    <>
      <div className="mb-4">
        <AutoGrowingTextarea
          label="Prompt"
          placeholder="Describe what you want to generate..."
          name="prompt"
          value={params.prompt}
          onChange={handleParamChange}
          onEnterPress={kernel.current?.triggerGenerate}
          maxHeight={600}
          labelClassName="block text-lg font-semibold text-white mb-2"
        />
      </div>

      <div className="mb-4">
        <AutoGrowingTextarea
          label="Negative Prompt"
          placeholder="Properties to avoid."
          name="negative_prompt"
          value={params.negative_prompt}
          onChange={handleParamChange}
          maxHeight={600}
          labelClassName="block text-lg font-semibold text-white mb-2"
        />
      </div>

      <div className="mb-4">
        <RangeControl
          label="Duration"
          name="duration"
          min={3}
          max={15}
          step={1}
          value={params.duration}
          onChange={handleParamChange}
          description={'In seconds.'}
        />
      </div>

      <div className="mb-4">
        <Select
          id="kling3-omni-resolution"
          label="Resolution"
          value={params.resolution}
          options={RESOLUTION_OPTIONS}
          onChange={handleResolutionChange}
        />
      </div>

      <div className="mb-4">
        <Select
          id="kling3-omni-reference-input-mode"
          label="Reference Input"
          value={referenceInputMode}
          options={REFERENCE_INPUT_MODES}
          onChange={handleReferenceInputModeChange}
        />
      </div>

      {referenceInputMode === 'video' && (
        <>
          <div className="mb-4">
            <AppTooltip
              content={VIDEO_REFERENCE_TYPE_HINT}
              side="left"
              contentClassName={KLING_TOOLTIP_CLASS}
            >
              <div>
                <Select
                  id="kling3-omni-video-ref-type"
                  label="Video Reference Type"
                  value={params.video_reference_type}
                  options={VIDEO_REFERENCE_TYPE_OPTIONS}
                  onChange={handleVideoReferenceTypeChange}
                />
              </div>
            </AppTooltip>
          </div>
          <VideoInputRangeControls
            className="mb-4"
            startMs={params.video_input_start_ms}
            endMs={params.video_input_end_ms}
            onChange={handleVideoInputRangeChange}
          />
        </>
      )}

      <div className="flex items-center space-x-2 mb-4">
        <Switch
          checked={params.generate_audio}
          onChange={onSwitchGenerateAudio}
          labelRight="Generate Audio"
        />
      </div>

      <div className="flex items-center space-x-2 mb-4">
        <Switch
          checked={params.keep_original_sound}
          onChange={onSwitchKeepOriginalSound}
          labelRight="Keep Original Sound"
        />
      </div>
    </>
  );
});
Kling3OmniControlPanel.displayName = 'Kling3OmniControlPanel';

const KlingControlPanel: React.FC<KlingControlPanelProps> = React.memo(({
  params, setModelParams, diffusionModel,
}) => {
  if (diffusionModel === 'kling-3') {
    return <Kling3ControlPanel params={params as Kling3Params} setModelParams={setModelParams as any} diffusionModel={diffusionModel} />;
  }
  if (diffusionModel === 'kling-3-omni') {
    return <Kling3OmniControlPanel params={params as Kling3OmniParams} setModelParams={setModelParams as any} diffusionModel={diffusionModel} />;
  }
  return <div>Unsupported Kling model: {diffusionModel}</div>;
});

KlingControlPanel.displayName = 'KlingControlPanel';

export default KlingControlPanel;
